import type {Message, StateAction} from '@microsoft/agent-host-protocol'
import type {PreparedPrompt, PreparedPromptTransform} from './contracts'
import {mergeContributedInstructionsMetadata} from './contributed-instructions'

function equal(left: unknown, right: unknown): boolean {
  if (left === right) return true
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false
  if (Array.isArray(left)) {
    return (
      Array.isArray(right) && left.length === right.length && left.every((value, index) => equal(value, right[index]))
    )
  }
  if (Array.isArray(right)) return false
  const expected = Object.entries(left).filter(([, value]) => value !== undefined)
  const actual = Object.entries(right).filter(([, value]) => value !== undefined)
  return (
    expected.length === actual.length &&
    expected.every(([key, value]) =>
      actual.some(([actualKey, actualValue]) => key === actualKey && equal(value, actualValue)),
    )
  )
}

function snapshot(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snapshot)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, snapshot(item)]))
  }
  return value
}

export function snapshotCommandPayload(prompt: PreparedPrompt) {
  return {
    text: prompt.text,
    model: snapshot(prompt.model),
    agent: snapshot(prompt.agent),
    attachments: snapshot(prompt.attachments ?? []),
    contributedInstructions: snapshot(prompt.contributedInstructions),
  }
}

export function snapshotNativeCommandPayload(message: Message) {
  return {
    ...snapshotCommandPayload(message),
    contributedInstructions: snapshot(message._meta?.['github.contributedInstructions']),
  }
}

export function canPrepareCommandPayload(
  expected: ReturnType<typeof snapshotCommandPayload>,
  source: PreparedPrompt,
  target: PreparedPrompt,
  transform?: PreparedPromptTransform,
): boolean {
  const sourceMatches =
    expected.text === source.text &&
    equal(expected.attachments, source.attachments ?? []) &&
    (expected.model === undefined || equal(expected.model, source.model)) &&
    (expected.agent === undefined || equal(expected.agent, source.agent)) &&
    (expected.contributedInstructions === undefined ||
      equal(expected.contributedInstructions, source.contributedInstructions))
  const preservesInstructions =
    source.contributedInstructions === undefined ||
    equal(source.contributedInstructions, target.contributedInstructions) ||
    (transform === 'merge_contributed_instructions' &&
      equal(
        mergeContributedInstructionsMetadata(source.contributedInstructions, target.contributedInstructions ?? []),
        target.contributedInstructions,
      ))
  return (
    sourceMatches &&
    source.text === target.text &&
    equal(source.attachments ?? [], target.attachments ?? []) &&
    (source.model === undefined || equal(source.model, target.model)) &&
    (source.agent === undefined || equal(source.agent, target.agent)) &&
    preservesInstructions
  )
}

export function matchesCommandPayload(
  expected: ReturnType<typeof snapshotCommandPayload>,
  action: StateAction,
): boolean {
  if (action.type !== 'chat/turnStarted' && action.type !== 'chat/pendingMessageSet') return false
  return matchesNativeCommandPayload(expected, action.message)
}

export function matchesNativeCommandPayload(
  expected: ReturnType<typeof snapshotCommandPayload>,
  actual: Message,
): boolean {
  return (
    expected.text === actual.text &&
    equal(expected.model, actual.model) &&
    equal(expected.agent, actual.agent) &&
    equal(expected.attachments, actual.attachments ?? []) &&
    equal(expected.contributedInstructions, actual._meta?.['github.contributedInstructions'])
  )
}
